import type { JsonObject, ToolDef } from "@fabric-harness/sdk";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

export interface DatabricksGenieAgentModeClientOptions {
  /** Explicit acknowledgement that Databricks currently labels this API Beta. */
  acknowledgeBeta: true;
  /** A 32-character lowercase hexadecimal Genie Agent id. */
  agentId: string;
  /** Maximum UTF-8 bytes accepted for one SSE event. Default 2 MiB. */
  maxEventBytes?: number;
  /** Maximum events accepted in one response stream. Default 10,000. */
  maxEvents?: number;
  /** Overall response deadline in milliseconds. Defaults to Databricks' 90-minute ceiling. */
  timeoutMs?: number;
  /** Maximum silence between SSE chunks. Defaults to 2 minutes. */
  idleTimeoutMs?: number;
  /** Server-side progress/lineage sink. Events are never inserted into model context. */
  onEvent?: (event: DatabricksGenieAgentModeEvent) => void | Promise<void>;
}

export interface DatabricksGenieAgentModeToolOptions {
  /** Maximum serialized terminal response exposed to the model. Default 1 MiB. */
  maxOutputBytes?: number;
}

export interface DatabricksGenieAgentModeResponse {
  object?: string;
  id: string;
  model?: string;
  status: "in_progress" | "completed" | "failed" | string;
  conversationId: string;
  createdAt?: number;
  output: Array<Record<string, unknown>>;
  error?: DatabricksGenieAgentModeErrorInfo;
}

export interface DatabricksGenieAgentModeErrorInfo {
  type?: string;
  code?: string;
  message?: string;
}

interface DatabricksGenieAgentModeEventBase {
  sequenceNumber: number;
  /** Original event payload for forward-compatible access to Beta fields. */
  raw: Record<string, unknown>;
}

export interface DatabricksGenieAgentModeCreatedEvent extends DatabricksGenieAgentModeEventBase {
  type: "response.created";
  response: DatabricksGenieAgentModeResponse;
}

export interface DatabricksGenieAgentModeOutputEvent extends DatabricksGenieAgentModeEventBase {
  type: "response.output_item.added" | "response.output_item.updated" | "response.output_item.done";
  outputIndex?: number;
  item: Record<string, unknown>;
}

export interface DatabricksGenieAgentModeCompletedEvent extends DatabricksGenieAgentModeEventBase {
  type: "response.completed";
  response: DatabricksGenieAgentModeResponse;
}

export interface DatabricksGenieAgentModeFailedEvent extends DatabricksGenieAgentModeEventBase {
  type: "response.failed";
  response: DatabricksGenieAgentModeResponse;
}

export interface DatabricksGenieAgentModeUnknownEvent extends DatabricksGenieAgentModeEventBase {
  type: "unknown";
  eventType: string;
}

export type DatabricksGenieAgentModeEvent =
  | DatabricksGenieAgentModeCreatedEvent
  | DatabricksGenieAgentModeOutputEvent
  | DatabricksGenieAgentModeCompletedEvent
  | DatabricksGenieAgentModeFailedEvent
  | DatabricksGenieAgentModeUnknownEvent;

export interface DatabricksGenieAgentModeItemPage {
  object?: string;
  data: Array<Record<string, unknown>>;
  status?: string;
  firstId?: string;
  lastId?: string;
  hasMore?: boolean;
}

export class DatabricksGenieAgentModeProtocolError extends Error {
  constructor(message: string) {
    super(`Databricks Genie Agent Mode protocol error: ${message}`);
    this.name = "DatabricksGenieAgentModeProtocolError";
  }
}

export class DatabricksGenieAgentModeResponseError extends Error {
  readonly response: DatabricksGenieAgentModeResponse;

  constructor(response: DatabricksGenieAgentModeResponse) {
    const details = [response.error?.code, response.error?.message].filter(Boolean).join(": ");
    super(
      `Databricks Genie Agent Mode response ${response.id} failed${details ? `: ${details}` : "."}`,
    );
    this.name = "DatabricksGenieAgentModeResponseError";
    this.response = response;
  }
}

export class DatabricksGenieAgentModeTimeoutError extends Error {
  readonly phase: "overall" | "idle";

  constructor(phase: "overall" | "idle", timeoutMs: number) {
    super(`Databricks Genie Agent Mode ${phase} timeout after ${timeoutMs}ms.`);
    this.name = "DatabricksGenieAgentModeTimeoutError";
    this.phase = phase;
  }
}

export class DatabricksGenieAgentModeClient {
  private readonly agentId: string;
  private readonly maxEventBytes: number;
  private readonly maxEvents: number;
  private readonly timeoutMs: number;
  private readonly idleTimeoutMs: number;
  private readonly onEvent?: DatabricksGenieAgentModeClientOptions["onEvent"];

  constructor(
    private readonly client: DatabricksRawProtocolClient,
    options: DatabricksGenieAgentModeClientOptions,
  ) {
    if (options.acknowledgeBeta !== true)
      throw new Error("Databricks Genie Agent Mode requires acknowledgeBeta: true.");
    this.agentId = validateAgentId(options.agentId);
    this.maxEventBytes = positiveInteger(options.maxEventBytes ?? 2 * 1024 * 1024, "maxEventBytes");
    this.maxEvents = positiveInteger(options.maxEvents ?? 10_000, "maxEvents");
    this.timeoutMs = positiveInteger(options.timeoutMs ?? 90 * 60_000, "timeoutMs");
    this.idleTimeoutMs = positiveInteger(options.idleTimeoutMs ?? 2 * 60_000, "idleTimeoutMs");
    this.onEvent = options.onEvent;
  }

  /**
   * Start or continue one Beta Agent Mode response. A failed terminal event is yielded and then
   * raises `DatabricksGenieAgentModeResponseError` on the next iterator step.
   */
  async *respond(
    question: string,
    options: { conversationId?: string; signal?: AbortSignal } = {},
  ): AsyncGenerator<DatabricksGenieAgentModeEvent, void, void> {
    const controller = new AbortController();
    let overallTimedOut = false;
    const overallTimer = setTimeout(() => {
      overallTimedOut = true;
      controller.abort();
    }, this.timeoutMs);
    const abortFromCaller = () => controller.abort(options.signal?.reason);
    options.signal?.addEventListener("abort", abortFromCaller, { once: true });
    try {
      const response = await this.client.postRaw(
        `/api/2.0/genie/agents/${encodeURIComponent(this.agentId)}/responses`,
        {
          input: [
            {
              type: "message",
              role: "user",
              content: [{ type: "input_text", text: nonEmpty(question, "question") }],
            },
          ],
          ...optional(
            "conversation_id",
            optionalNonEmpty(options.conversationId, "conversationId"),
          ),
        },
        {},
        { retry: "never", signal: controller.signal, accept: "text/event-stream" },
      );
      const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
      if (!contentType.includes("text/event-stream")) {
        await response.body?.cancel();
        throw new DatabricksGenieAgentModeProtocolError(
          `expected text/event-stream, received ${contentType || "no content type"}`,
        );
      }
      if (!response.body)
        throw new DatabricksGenieAgentModeProtocolError("response did not contain a body");

      let previousSequence = -1;
      let eventCount = 0;
      let terminal = false;
      for await (const frame of parseSse(response.body, this.maxEventBytes, this.idleTimeoutMs)) {
        if (frame.data === "[DONE]") continue;
        eventCount += 1;
        if (eventCount > this.maxEvents)
          throw new DatabricksGenieAgentModeProtocolError(
            `stream exceeded the configured ${this.maxEvents} event limit`,
          );
        const raw = parseEventData(frame.data);
        const type = requiredString(raw.type, "event type");
        if (frame.event && frame.event !== type) {
          throw new DatabricksGenieAgentModeProtocolError(
            `SSE event '${frame.event}' did not match payload type '${type}'`,
          );
        }
        const sequenceNumber = requiredSequence(raw.sequence_number);
        if (sequenceNumber <= previousSequence) {
          throw new DatabricksGenieAgentModeProtocolError(
            `sequence_number ${sequenceNumber} followed ${previousSequence}`,
          );
        }
        previousSequence = sequenceNumber;
        const event = parseAgentModeEvent(type, sequenceNumber, raw);
        await this.onEvent?.(event);
        yield event;
        if (event.type === "response.completed") {
          terminal = true;
          return;
        }
        if (event.type === "response.failed") {
          terminal = true;
          throw new DatabricksGenieAgentModeResponseError(event.response);
        }
      }
      if (!terminal)
        throw new DatabricksGenieAgentModeProtocolError(
          "stream ended before response.completed or response.failed",
        );
    } catch (error) {
      if (overallTimedOut)
        throw new DatabricksGenieAgentModeTimeoutError("overall", this.timeoutMs);
      throw error;
    } finally {
      clearTimeout(overallTimer);
      options.signal?.removeEventListener("abort", abortFromCaller);
    }
  }

  /** Consume a stream and return only its bounded terminal response. */
  async complete(
    question: string,
    options: { conversationId?: string; signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieAgentModeResponse> {
    let terminal: DatabricksGenieAgentModeResponse | undefined;
    for await (const event of this.respond(question, options)) {
      if (event.type === "response.completed") terminal = event.response;
    }
    if (!terminal)
      throw new DatabricksGenieAgentModeProtocolError("completed stream had no terminal response");
    return terminal;
  }

  async listConversationItems(
    conversationId: string,
    options: { after?: string; limit?: number; signal?: AbortSignal } = {},
  ): Promise<DatabricksGenieAgentModeItemPage> {
    const raw = await this.client.get<Record<string, unknown>>(
      `/api/2.0/genie/agents/${encodeURIComponent(this.agentId)}/conversations/${encodeURIComponent(nonEmpty(conversationId, "conversationId"))}/items`,
      {
        after: optionalNonEmpty(options.after, "after"),
        limit: options.limit === undefined ? undefined : positiveInteger(options.limit, "limit"),
      },
      signalOption(options.signal),
    );
    return {
      ...optional("object", asString(raw.object)),
      data: recordArray(raw.data),
      ...optional("status", asString(raw.status)),
      ...optional("firstId", asString(raw.first_id)),
      ...optional("lastId", asString(raw.last_id)),
      ...optional("hasMore", asBoolean(raw.has_more)),
    };
  }
}

export function databricksGenieAgentMode(
  client: DatabricksRawProtocolClient,
  options: DatabricksGenieAgentModeClientOptions,
): DatabricksGenieAgentModeClient {
  return new DatabricksGenieAgentModeClient(client, options);
}

export function databricksGenieAgentModeTool(
  client: DatabricksGenieAgentModeClient,
  agentId: string,
  options: DatabricksGenieAgentModeToolOptions = {},
): ToolDef {
  const configuredAgentId = validateAgentId(agentId);
  const maxOutputBytes = positiveInteger(options.maxOutputBytes ?? 1024 * 1024, "maxOutputBytes");
  return {
    name: "databricks_genie_agent_mode",
    description:
      "Ask a Databricks Genie Agent through the Beta Agent Mode streaming API and return its terminal response.",
    inputSchema: {
      type: "object",
      additionalProperties: false,
      properties: {
        agentId: { type: "string", const: configuredAgentId },
        question: { type: "string" },
        conversationId: { type: "string" },
      },
      required: ["agentId", "question"],
    },
    metadata: {
      effect: "execute",
      provider: "databricks",
      service: "genie-agent-mode",
      governance: { resources: [{ kind: "genie-agent", path: "/agentId" }] },
    },
    execute: async (input: JsonObject, context) => {
      if (input.agentId !== configuredAgentId)
        throw new Error("Databricks Genie Agent Mode tool agentId did not match its binding.");
      const response = await client.complete(String(input.question), {
        ...(typeof input.conversationId === "string"
          ? { conversationId: input.conversationId }
          : {}),
        ...(context?.signal ? { signal: context.signal } : {}),
      });
      if (new TextEncoder().encode(JSON.stringify(response)).byteLength > maxOutputBytes)
        throw new DatabricksGenieAgentModeProtocolError(
          `terminal response exceeded the configured ${maxOutputBytes}-byte model output limit`,
        );
      return response;
    },
  };
}

function parseAgentModeEvent(
  type: string,
  sequenceNumber: number,
  raw: Record<string, unknown>,
): DatabricksGenieAgentModeEvent {
  const base = { type, sequenceNumber, raw };
  if (type === "response.created" || type === "response.completed" || type === "response.failed") {
    return { ...base, type, response: parseResponse(raw.response) };
  }
  if (
    type === "response.output_item.added" ||
    type === "response.output_item.updated" ||
    type === "response.output_item.done"
  ) {
    return {
      ...base,
      type,
      ...optional("outputIndex", asNumber(raw.output_index)),
      item: requireRecord(raw.item, "output item"),
    };
  }
  return { ...base, type: "unknown", eventType: type };
}

function parseResponse(value: unknown): DatabricksGenieAgentModeResponse {
  const raw = requireRecord(value, "response");
  return {
    ...optional("object", asString(raw.object)),
    id: requiredString(raw.id, "response id"),
    ...optional("model", asString(raw.model)),
    status: requiredString(raw.status, "response status"),
    conversationId: requiredString(raw.conversation_id, "response conversation_id"),
    ...optional("createdAt", asNumber(raw.created_at)),
    output: recordArray(raw.output),
    ...optional("error", parseError(raw.error)),
  };
}

function parseError(value: unknown): DatabricksGenieAgentModeErrorInfo | undefined {
  if (value === undefined || value === null) return undefined;
  const raw = requireRecord(value, "response error");
  return {
    ...optional("type", asString(raw.type)),
    ...optional("code", asString(raw.code)),
    ...optional("message", asString(raw.message)),
  };
}

async function* parseSse(
  body: ReadableStream<Uint8Array>,
  maxEventBytes: number,
  idleTimeoutMs: number,
): AsyncGenerator<{ event?: string; data: string }> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let eventName: string | undefined;
  let data: string[] = [];
  let eventBytes = 0;

  const processLine = (line: string): { event?: string; data: string } | undefined => {
    if (line === "") {
      if (data.length === 0) {
        eventName = undefined;
        eventBytes = 0;
        return undefined;
      }
      const frame = { ...optional("event", eventName), data: data.join("\n") };
      eventName = undefined;
      data = [];
      eventBytes = 0;
      return frame;
    }
    if (line.startsWith(":")) return undefined;
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") eventName = value;
    if (field === "data") data.push(value);
    eventBytes += new TextEncoder().encode(line).byteLength + 1;
    if (eventBytes > maxEventBytes)
      throw new DatabricksGenieAgentModeProtocolError(
        `SSE event exceeded the configured ${maxEventBytes}-byte limit`,
      );
    return undefined;
  };

  try {
    for (;;) {
      const chunk = await readWithTimeout(reader, idleTimeoutMs);
      buffer += decoder.decode(chunk.value, { stream: !chunk.done });
      let newline = buffer.indexOf("\n");
      while (newline >= 0) {
        let line = buffer.slice(0, newline);
        buffer = buffer.slice(newline + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);
        const frame = processLine(line);
        if (frame) yield frame;
        newline = buffer.indexOf("\n");
      }
      if (buffer.length > maxEventBytes)
        throw new DatabricksGenieAgentModeProtocolError(
          `SSE event exceeded the configured ${maxEventBytes}-byte limit`,
        );
      if (chunk.done) break;
    }
    if (buffer) {
      const frame = processLine(buffer.endsWith("\r") ? buffer.slice(0, -1) : buffer);
      if (frame) yield frame;
    }
    const finalFrame = processLine("");
    if (finalFrame) yield finalFrame;
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

async function readWithTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeoutMs: number,
): ReturnType<ReadableStreamDefaultReader<Uint8Array>["read"]> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      reader.read(),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () => reject(new DatabricksGenieAgentModeTimeoutError("idle", timeoutMs)),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function parseEventData(data: string): Record<string, unknown> {
  let value: unknown;
  try {
    value = JSON.parse(data);
  } catch {
    throw new DatabricksGenieAgentModeProtocolError("SSE data was not valid JSON");
  }
  return requireRecord(value, "event payload");
}

function requireRecord(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    throw new DatabricksGenieAgentModeProtocolError(`${name} must be an object`);
  return value as Record<string, unknown>;
}

function recordArray(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> =>
      typeof item === "object" && item !== null && !Array.isArray(item),
  );
}

function requiredString(value: unknown, name: string): string {
  if (typeof value !== "string" || !value.trim())
    throw new DatabricksGenieAgentModeProtocolError(`${name} is missing`);
  return value;
}

function requiredSequence(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0)
    throw new DatabricksGenieAgentModeProtocolError(
      "sequence_number must be a non-negative integer",
    );
  return value;
}

function validateAgentId(value: string): string {
  const normalized = value.trim();
  if (!/^[0-9a-f]{32}$/.test(normalized))
    throw new Error(
      "Databricks Genie Agent Mode agentId must be 32 lowercase hexadecimal characters.",
    );
  return normalized;
}

function nonEmpty(value: string, name: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error(`Databricks Genie Agent Mode ${name} cannot be empty.`);
  return normalized;
}

function optionalNonEmpty(value: string | undefined, name: string): string | undefined {
  return value === undefined ? undefined : nonEmpty(value, name);
}

function positiveInteger(value: number, name: string): number {
  if (!Number.isSafeInteger(value) || value <= 0)
    throw new Error(`Databricks Genie Agent Mode ${name} must be a positive integer.`);
  return value;
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function signalOption(signal?: AbortSignal): { signal?: AbortSignal } {
  return signal ? { signal } : {};
}

function optional<Key extends string, Value>(
  key: Key,
  value: Value | undefined,
): Partial<Record<Key, Value>> {
  return value === undefined ? {} : ({ [key]: value } as Record<Key, Value>);
}
